import { useCallback } from 'react';
import { useCountryCodeValue, useIpValue } from './system/useCountryCode';
import { useOrgConfigStateValue } from './useOrgConfig';
import { logger } from 'services/logger';
import { getProxyOriginSrc, isTiktokProxySrc } from 'lib/utils/platformImage';
import { isURL } from 'utils/is';
import wait from 'utils/wait';

const proxyRef = { current: false };

Promise.race([
  fetch('https://v16m-default.tiktokcdn.com'),
  wait(3000).then(() => Promise.reject(new Error('timeout'))),
])
  .then(() => {
    proxyRef.current = true;
  })
  .catch(() => {
    proxyRef.current = false;
  });

function useProxySrc() {
  const countryCode = useCountryCodeValue();

  const ip = useIpValue();

  const { tiktokImageProxyOriginList } = useOrgConfigStateValue();

  const getProxyEnable = useCallback(
    (src: string) => {
      try {
        const { origin } = new URL(src);
        return (
          !proxyRef.current &&
          countryCode === 'CN' &&
          tiktokImageProxyOriginList?.some((_) => origin.includes(_))
        );
      } catch (_) {
        return false;
      }
    },
    [countryCode, tiktokImageProxyOriginList],
  );

  const errorLog = useCallback(
    (src: string) => {
      const originSrc = isTiktokProxySrc(src) ? getProxyOriginSrc(src) : src;
      logger.info(
        `[loadImageError] ${JSON.stringify({
          ip,
          countryCode,
          src,
          originSrc,
        })}`,
      );
    },
    [ip, countryCode],
  );

  const getOriginSrc = useCallback(
    (src: string) => {
      const [, , partial] = src.split('http');
      if (!partial) {
        return src;
      }
      const originSrc = `http${partial}`;
      if (!isURL(originSrc)) {
        return src;
      }
      if (getProxyEnable(originSrc)) {
        return src;
      }
      return originSrc;
    },
    [getProxyEnable],
  );

  return { getProxyEnable, errorLog, getOriginSrc };
}

export default useProxySrc;
